import React, { useState, useEffect, useRef, useCallback } from "react";
import { DEFAULT_BLOCK_CSS } from "../../../../constant/constant";
import { useApp } from "../../../../context/AppState";
import { parseCssStringToObject, resolveInlineFontFamily } from "../../../../helper";
import {
  COUNTDOWN_DISPLAY_LAYOUT,
  resolveCountdownDisplay,
  resolveCountdownRuntime,
  parseCountdownFixedEndDate,
  formatPillCountdown,
  shouldRenderCountdownUnits,
  getTimerPillStyleFromConfig,
  getUnitBlockStyle,
} from "../../../../utils/countdownDisplay";
import styles from "./CountdownBlock.module.css";

function pad2(n) {
  return String(n).padStart(2, "0");
}

const CountdownBlock = ({ popupData, section }) => {
  const { isMobile, appState } = useApp();
  const isPreview = !!appState?.is_preview_mode;
  const expiredCloseSentRef = useRef(false);
  const display = resolveCountdownDisplay(section, popupData);
  const countdownTimer = section?.setting?.countdown_timer;
  const runtime = resolveCountdownRuntime(popupData, section);
  const { countdown_type, values, conditions, timeUnitsOn } = runtime;
  const { days, hours, minutes, seconds, countdownEndTime } = values;
  const {
    isHideDays,
    isHideseconds,
    isHideMinutes,
    isHideHours,
    isResetTimer,
    isShowUnitLabels,
    isHideZeroUnits,
  } = conditions;
  const showUnitLabels = isShowUnitLabels !== false;
  const hideZeroUnits = isHideZeroUnits === true;
  const useUnitsDisplay = shouldRenderCountdownUnits(display, conditions, timeUnitsOn);

  const [endTime, setEndTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const movingDurationMs =
    (Number(days) || 0) * 86400000 +
    (Number(hours) || 0) * 3600000 +
    (Number(minutes) || 0) * 60000 +
    (Number(seconds) || 0) * 1000;

  useEffect(() => {
    expiredCloseSentRef.current = false;
  }, [
    countdown_type,
    countdownEndTime,
    days,
    hours,
    minutes,
    seconds,
    popupData?._id,
    countdownTimer?.timer_type,
    countdownTimer?.fixed_options?.end_datetime,
    countdownTimer?.session_options?.duration,
  ]);

  useEffect(() => {
    if (countdown_type === "moving_date") {
      if (movingDurationMs <= 0) {
        setEndTime(null);
        return;
      }
      setEndTime(new Date(Date.now() + movingDurationMs));
      return;
    }
    setEndTime(parseCountdownFixedEndDate(countdownEndTime));
  }, [countdown_type, countdownEndTime, movingDurationMs]);

  const updateTimeLeft = useCallback(() => {
    if (!endTime) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      return;
    }

    const totalSeconds = Math.floor((endTime.getTime() - Date.now()) / 1000);

    if (totalSeconds <= 0) {
      setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });

      if (
        countdown_type === "moving_date" &&
        isResetTimer &&
        movingDurationMs > 0
      ) {
        setEndTime(new Date(Date.now() + movingDurationMs));
        return;
      }

      if (!isPreview && !expiredCloseSentRef.current && popupData?._id) {
        expiredCloseSentRef.current = true;
        window.postMessage(
          {
            type: "POPUP_CLOSE",
            props: { popupValue: popupData, reason: "countdown_expired" },
          },
          "*"
        );
      }
      return;
    }

    let totalHours = Math.floor(totalSeconds / 3600);
    const remMinutes = Math.floor((totalSeconds % 3600) / 60);
    const remSeconds = totalSeconds % 60;
    let d = 0;
    let h = totalHours;

    if (!isHideDays) {
      d = Math.floor(totalHours / 24);
      h = totalHours % 24;
    }

    setTimeLeft({ days: d, hours: h, minutes: remMinutes, seconds: remSeconds });
  }, [
    endTime,
    isHideDays,
    countdown_type,
    isResetTimer,
    movingDurationMs,
    isPreview,
    popupData,
  ]);

  useEffect(() => {
    updateTimeLeft();
    const timer = setInterval(updateTimeLeft, 500);
    return () => clearInterval(timer);
  }, [updateTimeLeft]);

  const blockCSS = {
    ...DEFAULT_BLOCK_CSS,
    ...section?.setting?.customCSS?.[isMobile ? "mobile" : "desktop"],
    ...parseCssStringToObject(
      section?.setting?.overrideCSS?.[isMobile ? "mobile" : "desktop"]
    ),
    boxSizing: "border-box",
  };

  const resolvedFontFamily = resolveInlineFontFamily(blockCSS.fontFamily);
  // Font-family only — do not blanket-apply blockCSS.fontSize (title size).
  // Title vs timer digit sizes are separate design fields.
  const editorTypography = {
    ...(resolvedFontFamily ? { fontFamily: resolvedFontFamily } : {}),
  };

  const countdownValues = values;
  const unitTextKeys = {
    days: "dayText",
    hours: "hourText",
    minutes: "minuteText",
    seconds: "secondText",
  };
  const labelForUnit = (key) => {
    const row = countdownTimer?.units?.[key];
    const fromValues = countdownValues[unitTextKeys[key]];
    const custom = row?.label ?? fromValues;
    return custom != null ? String(custom).trim() : "";
  };
  const showLabelForUnit = (key) => showUnitLabels && labelForUnit(key) !== "";
  const dayLb = labelForUnit("days");
  const hourLb = labelForUnit("hours");
  const minLb = labelForUnit("minutes");
  const secLb = labelForUnit("seconds");

  const clockVariant = display.clockVariant || "pill";
  const unitBlockStyle = getUnitBlockStyle(countdownTimer);
  const labelColor =
    countdownTimer?.style?.labels?.color || display.textColor || "#64748b";

  const urgencyBarOn = display.urgencyBarEnabled !== false;
  const rootStyle = {
    ...blockCSS,
    ...(resolvedFontFamily ? { fontFamily: resolvedFontFamily } : {}),
    width: "100%",
    ...(display.layout === COUNTDOWN_DISPLAY_LAYOUT.BAR &&
    urgencyBarOn &&
    display.backgroundColor
      ? { backgroundColor: display.backgroundColor }
      : {}),
    ...(display.borderRadius != null && display.borderRadius !== ""
      ? { borderRadius: display.borderRadius }
      : {}),
    ...(!urgencyBarOn ? { backgroundColor: "transparent", padding: 0 } : {}),
  };

  const messageStyle = {
    fontSize: display.textFontSize || blockCSS.fontSize || "14px",
    fontWeight: display.textFontWeight || "600",
    color: display.textColor || blockCSS.color || "#ffffff",
    lineHeight: 1.35,
    ...editorTypography,
  };

  if (countdown_type === "till_fixed_date" && !parseCountdownFixedEndDate(countdownEndTime)) {
    return (
      <div className={styles.root} style={rootStyle}>
        <div className={styles.placeholder}>
          Choose an <strong>Offer ends</strong> date in the countdown settings to show the timer.
        </div>
      </div>
    );
  }

  if (display.stockMode) {
    const stock = countdownTimer?.stock_options || {};
    const count = stock.manual_stock_count ?? 5;
    const label = stock.unit_label || "left in stock";
    return (
      <div className={styles.root} style={rootStyle}>
        <div className={styles.bar} style={{ justifyContent: "center" }}>
          <span className={styles.barMessage} style={messageStyle}>
            Only {count} {label}
          </span>
        </div>
      </div>
    );
  }

  if (countdown_type === "moving_date" && movingDurationMs <= 0) {
    return (
      <div className={styles.root} style={rootStyle}>
        <div className={styles.placeholder}>
          Set at least one of days, hours, minutes, or seconds for the per-visitor countdown.
        </div>
      </div>
    );
  }

  const expired = endTime != null && endTime.getTime() <= Date.now();

  if (expired && isPreview) {
    return (
      <div className={styles.root} style={{ ...rootStyle, justifyContent: "center" }}>
        <span
          className={styles.expired}
          style={{
            color: display.textColor || blockCSS.color || "#475569",
            border: "none",
            background: "transparent",
          }}
        >
          Offer ended
        </span>
      </div>
    );
  }

  if (expired && !isPreview) {
    return null;
  }

  const pillStyle = {
    ...getTimerPillStyleFromConfig(display, countdownTimer, isMobile),
    ...editorTypography,
  };
  const pillTimerText = () =>
    formatPillCountdown(timeLeft, conditions, { compactWhenUnitsOff: !timeUnitsOn });

  const initialTotalSeconds = Math.max(
    1,
    Math.floor(movingDurationMs / 1000) ||
      (Number(days) || 0) * 86400 +
        (Number(hours) || 0) * 3600 +
        (Number(minutes) || 0) * 60 +
        (Number(seconds) || 0)
  );
  const currentTotalSeconds = Math.max(
    0,
    (Number(timeLeft.days) || 0) * 86400 +
      (Number(timeLeft.hours) || 0) * 3600 +
      (Number(timeLeft.minutes) || 0) * 60 +
      (Number(timeLeft.seconds) || 0)
  );
  const progressPct = Math.min(
    100,
    Math.max(0, (currentTotalSeconds / initialTotalSeconds) * 100)
  );

  const shouldShowUnit = (value, unitKey) => {
    if (isHideDays && unitKey === "days") return false;
    if (isHideHours && unitKey === "hours") return false;
    if (isHideMinutes && unitKey === "minutes") return false;
    if (isHideseconds && unitKey === "seconds") return false;
    if (hideZeroUnits && Number(value) === 0) return false;
    return true;
  };

  const renderPill = () => (
    <span className={styles.pill} style={pillStyle}>
      {pillTimerText()}
    </span>
  );

  const unitsShellClass = [
    styles.unitsShell,
    clockVariant === "blocks" && styles.unitsShellBlocks,
    clockVariant === "minimal" && styles.unitsShellMinimal,
    clockVariant === "flip" && styles.unitsShellFlip,
    clockVariant === "circular" && styles.unitsShellCircular,
  ]
    .filter(Boolean)
    .join(" ");

  const digitStyle = {
    ...editorTypography,
    color: display.digitColor || blockCSS.color || "var(--we-text-primary, #0f172a)",
    fontSize: display.digitFontSize || "16px",
  };
  const unitLabelStyle = { color: labelColor, ...editorTypography };
  const barUnitValueStyle = {
    ...editorTypography,
    color: display.digitColor || display.textColor || "#ffffff",
    fontSize: display.digitFontSize || "16px",
  };
  const barUnitLabelStyle = {
    ...editorTypography,
    color: display.digitColor || labelColor || "rgba(255,255,255,0.88)",
    ...(display.digitFontSize
      ? { fontSize: `calc(${display.digitFontSize} * 0.45)` }
      : {}),
  };
  const barUnitSepStyle = {
    ...editorTypography,
    color: display.digitColor || display.textColor || "rgba(255,255,255,0.7)",
    ...(display.digitFontSize ? { fontSize: display.digitFontSize } : {}),
  };
  const useBlockUnit =
    clockVariant === "blocks" || clockVariant === "circular" || clockVariant === "flip";

  const renderUnit = (value, label, unitKey, inBar = false) => {
    const showLabel = showLabelForUnit(unitKey);
    if (inBar) {
      return (
        <div className={styles.barUnit}>
          <span className={styles.barUnitValue} style={barUnitValueStyle}>
            {pad2(value)}
          </span>
          {showLabel ? (
            <span className={styles.barUnitLabel} style={barUnitLabelStyle}>
              {label}
            </span>
          ) : null}
        </div>
      );
    }
    return (
      <div className={styles.unit} style={useBlockUnit ? unitBlockStyle : undefined}>
        <span className={styles.unitValue} style={digitStyle}>
          {pad2(value)}
        </span>
        {showLabel ? (
          <span className={styles.unitLabel} style={unitLabelStyle}>
            {label}
          </span>
        ) : null}
      </div>
    );
  };

  const showSep = (show, inBar = false) => show && (inBar || clockVariant !== "minimal");

  const renderUnitsRow = (inBar = false) => {
    const shellClass = inBar ? styles.barUnitsShell : unitsShellClass;
    const Sep = inBar ? styles.barUnitSep : styles.unitSep;

    return (
      <div className={shellClass}>
        {shouldShowUnit(timeLeft.days, "days") && (
          <>
            {renderUnit(timeLeft.days, dayLb, "days", inBar)}
            {showSep(
              shouldShowUnit(timeLeft.hours, "hours") ||
                shouldShowUnit(timeLeft.minutes, "minutes") ||
                shouldShowUnit(timeLeft.seconds, "seconds"),
              inBar
            ) && (
              <span className={Sep} style={barUnitSepStyle} aria-hidden>
                :
              </span>
            )}
          </>
        )}
        {shouldShowUnit(timeLeft.hours, "hours") && (
          <>
            {renderUnit(timeLeft.hours, hourLb, "hours", inBar)}
            {showSep(
              shouldShowUnit(timeLeft.minutes, "minutes") ||
                shouldShowUnit(timeLeft.seconds, "seconds"),
              inBar
            ) && (
              <span className={Sep} style={barUnitSepStyle} aria-hidden>
                :
              </span>
            )}
          </>
        )}
        {shouldShowUnit(timeLeft.minutes, "minutes") && (
          <>
            {renderUnit(timeLeft.minutes, minLb, "minutes", inBar)}
            {shouldShowUnit(timeLeft.seconds, "seconds") && (
              <span className={Sep} aria-hidden>
                :
              </span>
            )}
          </>
        )}
        {shouldShowUnit(timeLeft.seconds, "seconds") &&
          renderUnit(timeLeft.seconds, secLb, "seconds", inBar)}
      </div>
    );
  };

  /** Pill when time-units off; labeled unit boxes when on (urgency bar + {{timer}}). */
  const renderTimerDisplay = ({ inline = false } = {}) => {
    if (useUnitsDisplay) {
      const units = renderUnitsRow(true);
      if (inline) {
        return <span className={styles.inlineUnits}>{units}</span>;
      }
      return <div className={styles.barTimer}>{units}</div>;
    }
    return renderPill();
  };

  const renderUrgencyMessage = (text) => {
    if (!text || !String(text).includes("{{timer}}")) {
      return text ? <span className={styles.barMessage} style={messageStyle}>{text}</span> : null;
    }
    const parts = String(text).split("{{timer}}");
    return (
      <span
        className={styles.barMessage}
        style={{
          ...messageStyle,
          display: "inline-flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
        }}
      >
        {parts.map((part, i) => (
          <React.Fragment key={`urgency-part-${i}`}>
            {part ? <span>{part}</span> : null}
            {i < parts.length - 1 ? renderTimerDisplay({ inline: true }) : null}
          </React.Fragment>
        ))}
      </span>
    );
  };

  const showUrgency =
    urgencyBarOn && display.showUrgencyText !== false && display.urgencyText;
  const urgencyHasInlineTimer =
    showUrgency && String(display.urgencyText).includes("{{timer}}");

  if (display.layout === COUNTDOWN_DISPLAY_LAYOUT.BAR) {
    return (
      <div className={styles.root} style={rootStyle}>
        <div className={styles.bar}>
          {showUrgency ? (
            <div className={styles.barLead}>
              {renderUrgencyMessage(display.urgencyText)}
            </div>
          ) : null}
          {!urgencyHasInlineTimer ? renderTimerDisplay() : null}
        </div>
      </div>
    );
  }

  const renderProgressBar = () => (
    <div className={styles.progressWrap}>
      <div
        className={styles.progressTrack}
        style={{ backgroundColor: "rgba(255,255,255,0.25)" }}
        role="progressbar"
        aria-valuenow={Math.round(progressPct)}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={styles.progressFill}
          style={{
            width: `${progressPct}%`,
            backgroundColor: pillStyle.color || "#ffffff",
          }}
        />
      </div>
      <span className={styles.progressLabel} style={{ color: pillStyle.color || "#ffffff" }}>
        {pillTimerText()}
      </span>
    </div>
  );

  if (display.layout === COUNTDOWN_DISPLAY_LAYOUT.INLINE) {
    const showProgress = clockVariant === "bar";
    return (
      <div className={styles.root} style={rootStyle}>
        <div
          className={styles.inlineWrap}
          style={{
            flexDirection: showUrgency ? "column" : "row",
            alignItems: "stretch",
            gap: showUrgency ? 8 : 0,
            width: "100%",
          }}
        >
          {showUrgency ? renderUrgencyMessage(display.urgencyText) : null}
          {showProgress ? renderProgressBar() : !urgencyHasInlineTimer ? renderPill() : null}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root} style={{ ...rootStyle, flexDirection: "column", gap: 8 }}>
      {showUrgency ? (
        <div style={{ width: "100%", textAlign: "center" }}>
          {renderUrgencyMessage(display.urgencyText)}
        </div>
      ) : null}
      {renderUnitsRow()}
    </div>
  );
};

export default CountdownBlock;
